import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, errMsg, useFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ErrorState, EmptyState, StatusBadge, Pagination, Confirm } from "@/components/common";
import { DashHeader } from "@/dashboard/DashboardLayout";
import { EVENT_STATUS, ROLE_LABEL, fmtDateTime } from "@/lib/format";

export function Overview() {
  const { data, loading, error, reload } = useFetch("/admin/overview");
  if (loading && !data) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div data-testid="dash-overview">
      <DashHeader title="Panoramica" subtitle="Stato generale di eventi, classifiche e contenuti." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[["Clan", data.totals.clans], ["Utenti", data.totals.users], ["News", data.totals.news], ["Correzioni aperte", data.totals.open_corrections]].map(([l, v]) => (
          <div key={l} className="nx-card p-5"><div className="text-[10px] uppercase tracking-widest text-[#C6C6CE]/50">{l}</div><div className="font-num text-3xl font-bold text-[#F5F5F7] mt-1">{v}</div></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {Object.entries(data.counts).map(([k, v]) => <div key={k} className="nx-card px-4 py-2 text-xs flex items-center gap-2"><span className="text-[#C6C6CE]/60">{EVENT_STATUS[k]}</span><span className="font-num text-[#F5F5F7]">{v}</span></div>)}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        {[["Eventi attivi", data.active], ["Eventi in programma", data.upcoming]].map(([title, list]) => (
          <div key={title} className="nx-card p-5">
            <div className="eyebrow mb-4">{title}</div>
            {list.length ? <ul className="space-y-3">{list.map((e) => <li key={e.id}><Link to={`/dashboard/eventi/${e.id}`} className="flex items-center justify-between gap-3 text-sm hover:text-[#962FFF]"><span className="truncate text-[#F5F5F7]">{e.title}</span><StatusBadge status={e.status} /></Link></li>)}</ul> : <p className="text-sm text-[#C6C6CE]/50">Nessun evento.</p>}
          </div>
        ))}
        <div className="nx-card p-5" data-testid="recent-result-changes">
          <div className="eyebrow mb-4">Ultime modifiche classifiche</div>
          {data.recent_result_changes.length ? <ul className="space-y-3 text-xs">{data.recent_result_changes.map((a) => <li key={a.id} className="border-b border-[#C6C6CE]/5 pb-2"><div className="text-[#F5F5F7]">{a.action} · {a.actor_name}</div><div className="text-[#C6C6CE]/50">{(a.after || a.before)?.registration_name} · {fmtDateTime(a.created_at)}</div></li>)}</ul> : <p className="text-sm text-[#C6C6CE]/50">Nessuna modifica recente.</p>}
        </div>
      </div>
    </div>
  );
}

function UserRow({ u, clans, me, onSaved }) {
  const [form, setForm] = useState({ role: u.role, clan_id: u.clan_id || "", can_publish_news: !!u.can_publish_news, active: u.active !== false, public_title: u.public_title || "" });
  const self = u.id === me.id;
  const verify = async () => {
    try {
      await api.post(`/admin/users/${u.id}/verify-email`);
      toast.success(`Email di ${u.email} confermata`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    try {
      const payload = { ...form, clan_id: form.clan_id || null };
      if (self) { delete payload.role; delete payload.active; }
      await api.patch(`/admin/users/${u.id}`, payload);
      toast.success("Utente aggiornato");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr className="border-b border-[#C6C6CE]/5 align-top" data-testid={`user-row-${u.email}`}>
      <td className="py-3 pr-3"><div className="text-[#F5F5F7]">{u.display_name}</div><div className="text-xs text-[#C6C6CE]/50">{u.email}</div>{u.email_verified === false && <div className="flex items-center gap-2 mt-1"><span data-testid={`user-unverified-${u.email}`} className="text-[10px] uppercase tracking-widest text-amber-300">Email non confermata</span><Confirm testId={`verify-${u.email}`} trigger={<button data-testid={`user-verify-btn-${u.email}`} className="btn-ghost btn-sm py-0.5 px-2 text-[10px]">Conferma email</button>} title="Confermare l'email manualmente?" description={`L'indirizzo ${u.email} verrà segnato come confermato senza link. L'operazione viene registrata nel registro attività.`} confirmLabel="Conferma email" onConfirm={verify} /></div>}</td>
      <td className="py-3 pr-3"><select data-testid={`user-role-${u.email}`} disabled={self} className="nx-input py-1.5 text-xs" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
      <td className="py-3 pr-3"><select data-testid={`user-clan-${u.email}`} className="nx-input py-1.5 text-xs" value={form.clan_id} onChange={(e) => setForm({ ...form, clan_id: e.target.value })}><option value="">—</option>{clans.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
      <td className="py-3 pr-3"><input className="nx-input py-1.5 text-xs" placeholder="Titolo pubblico" value={form.public_title} onChange={(e) => setForm({ ...form, public_title: e.target.value })} /></td>
      <td className="py-3 pr-3 text-xs space-y-1">
        <label className="flex items-center gap-2"><input data-testid={`user-news-${u.email}`} type="checkbox" checked={form.can_publish_news} onChange={(e) => setForm({ ...form, can_publish_news: e.target.checked })} /> Pubblica news</label>
        <label className="flex items-center gap-2"><input type="checkbox" disabled={self} checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Attivo</label>
      </td>
      <td className="py-3"><Confirm testId={`user-save-${u.email}`} trigger={<button className="btn-primary btn-sm" data-testid={`user-save-btn-${u.email}`}>Salva</button>} title="Confermi la modifica?" description={`Ruolo e permessi di ${u.email} verranno aggiornati e registrati nel registro attività.`} onConfirm={save} /></td>
    </tr>
  );
}

export function AdminUsers() {
  const { user } = useAuth();
  const [f, setF] = useState({ page: 1 });
  const { data, loading, error, reload } = useFetch("/admin/users", f);
  const clans = useFetch("/clans", { limit: 100 });
  return (
    <div data-testid="dash-users">
      <DashHeader title="Utenti e ruoli" subtitle="Solo il Super Admin può assegnare ruoli privilegiati." />
      <div className="flex flex-wrap gap-2.5 mb-5">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#C6C6CE]/40" /><input data-testid="users-search" placeholder="Cerca email, nome, gamertag" className="nx-input pl-9" onChange={(e) => setF({ ...f, q: e.target.value || undefined, page: 1 })} /></div>
        <select className="nx-input w-auto" aria-label="Ruolo" onChange={(e) => setF({ ...f, role: e.target.value || undefined, page: 1 })}><option value="">Tutti i ruoli</option>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : (
        <div className="nx-card p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-[10px] uppercase tracking-widest text-[#C6C6CE]/50 text-left"><th className="pb-3">Utente</th><th className="pb-3">Ruolo</th><th className="pb-3">Clan</th><th className="pb-3">Titolo staff</th><th className="pb-3">Permessi</th><th /></tr></thead>
            <tbody>{data.items.map((u) => <UserRow key={u.id + u.role + u.clan_id} u={u} clans={clans.data?.items || []} me={user} onSaved={reload} />)}</tbody>
          </table>
          <Pagination page={data.page} pages={data.pages} onChange={(p) => setF({ ...f, page: p })} />
        </div>
      )}
    </div>
  );
}

function CorrectionRow({ c, onSaved }) {
  const [form, setForm] = useState({ status: c.status, response: c.response || "" });
  const save = async () => {
    try {
      await api.patch(`/admin/corrections/${c.id}`, form);
      toast.success("Richiesta aggiornata");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="nx-card p-5 grid md:grid-cols-3 gap-4" data-testid={`correction-${c.id}`}>
      <div className="md:col-span-2">
        <div className="text-[#F5F5F7] font-semibold">{c.event_title}</div>
        <div className="text-xs text-[#C6C6CE]/50">{c.author_name} · {fmtDateTime(c.created_at)}</div>
        <p className="mt-2 text-sm text-[#C6C6CE]">{c.message}</p>
        <Link to={`/dashboard/eventi/${c.event_id}`} className="mt-2 inline-block text-xs text-[#962FFF] hover:underline">Apri evento</Link>
      </div>
      <div className="space-y-2">
        <select data-testid={`correction-status-${c.id}`} className="nx-input py-1.5 text-xs" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{["aperta", "in_revisione", "accolta", "respinta"].map((s) => <option key={s}>{s}</option>)}</select>
        <textarea className="nx-input text-xs" rows={2} placeholder="Risposta" value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} />
        <button data-testid={`correction-save-${c.id}`} onClick={save} className="btn-primary btn-sm">Aggiorna</button>
      </div>
    </div>
  );
}

export function AdminCorrections() {
  const { data, loading, error, reload } = useFetch("/admin/corrections");
  return (
    <div data-testid="dash-corrections">
      <DashHeader title="Richieste di correzione" subtitle="Segnalazioni inviate dai leader dei clan." />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.length ? <div className="space-y-3">{data.map((c) => <CorrectionRow key={c.id} c={c} onSaved={reload} />)}</div> : <EmptyState title="Nessuna richiesta" />}
    </div>
  );
}

const APP_STATUS = { in_attesa: "In attesa", approvata: "Approvata", respinta: "Respinta" };

function ApplicationCard({ a, isSuper, onSaved }) {
  const [response, setResponse] = useState("");
  const [kind, setKind] = useState("membro");
  const decide = async (status) => {
    try {
      const { data } = await api.post(`/admin/clan-applications/${a.id}/decision`, { status, response, kind });
      toast.success(status === "approvata" ? "Candidatura approvata: clan creato" : "Candidatura respinta");
      if (!data.email_sent) toast.warning("Email di notifica non inviata: il candidato vedrà l'esito nell'area personale.");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pending = a.status === "in_attesa";
  return (
    <div className="nx-card p-5 grid lg:grid-cols-3 gap-5" data-testid={`application-${a.id}`}>
      <div className="lg:col-span-2 space-y-2">
        <div className="flex flex-wrap items-center gap-3"><span className="font-num text-xs px-2 py-1 border border-[#C6C6CE]/20 rounded">{a.tag}</span><span className="text-[#F5F5F7] font-semibold">{a.clan_name}</span><span data-testid={`application-status-${a.id}`} className="text-[10px] uppercase tracking-widest text-[#d6b3ff]">{APP_STATUS[a.status]}</span></div>
        <div className="text-xs text-[#C6C6CE]/50">Inviata da {a.applicant_name} ({a.applicant_email}) · {fmtDateTime(a.created_at)} · Leader: {a.leader_name} · {a.members_count} membri</div>
        {a.discord && <a href={a.discord} target="_blank" rel="noopener noreferrer" className="text-xs text-[#962FFF] hover:underline break-all">{a.discord}</a>}
        <p className="text-sm text-[#C6C6CE] whitespace-pre-line"><span className="text-[#C6C6CE]/50">Presentazione: </span>{a.description}</p>
        <p className="text-sm text-[#C6C6CE] whitespace-pre-line"><span className="text-[#C6C6CE]/50">Motivazione: </span>{a.motivation}</p>
        {!pending && <p className="text-xs text-[#C6C6CE]/60">Valutata da {a.reviewed_by} · {fmtDateTime(a.reviewed_at)}{a.response ? ` · “${a.response}”` : ""}</p>}
        {a.status === "approvata" && !a.leader_linked && <p className="text-xs text-amber-200">Il leader autorizzato del clan va collegato dal Super Admin (Clan → Modifica).</p>}
      </div>
      {pending && (
        <div className="space-y-2">
          <select data-testid={`application-kind-${a.id}`} aria-label="Tipologia clan" className="nx-input py-1.5 text-xs" value={kind} onChange={(e) => setKind(e.target.value)}><option value="membro">Membro ufficiale</option><option value="ospite">Ospite</option><option value="evento">Partecipante evento</option></select>
          <textarea data-testid={`application-response-${a.id}`} className="nx-input text-xs" rows={3} placeholder="Messaggio per il candidato (opzionale)" value={response} onChange={(e) => setResponse(e.target.value)} />
          <div className="flex gap-2">
            <Confirm testId={`approve-${a.id}`} trigger={<button data-testid={`application-approve-${a.id}`} className="btn-primary btn-sm flex-1">Approva</button>} title="Approvare la candidatura?" description={`Verrà creato il clan ${a.clan_name} [${a.tag}]${isSuper ? " e il candidato diventerà leader autorizzato" : ""}. Il candidato riceverà un'email.`} confirmLabel="Approva" onConfirm={() => decide("approvata")} />
            <Confirm testId={`reject-${a.id}`} trigger={<button data-testid={`application-reject-${a.id}`} className="btn-danger">Respingi</button>} title="Respingere la candidatura?" description="Il candidato riceverà un'email con l'esito." confirmLabel="Respingi" onConfirm={() => decide("respinta")} />
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminApplications() {
  const { user } = useAuth();
  const [status, setStatus] = useState("in_attesa");
  const { data, loading, error, reload } = useFetch("/admin/clan-applications", status ? { status } : {});
  return (
    <div data-testid="dash-applications">
      <DashHeader title="Candidature clan" subtitle="Richieste di adesione inviate dal sito.">
        <select data-testid="applications-filter-status" aria-label="Stato" className="nx-input py-1.5 text-xs w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Tutte</option>{Object.entries(APP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </DashHeader>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.length ? <div className="space-y-3">{data.map((a) => <ApplicationCard key={a.id} a={a} isSuper={user.role === "super_admin"} onSaved={reload} />)}</div> : <EmptyState title="Nessuna candidatura" testId="applications-empty" />}
    </div>
  );
}

export function AdminAudit() {
  const [f, setF] = useState({ page: 1 });
  const { data, loading, error, reload } = useFetch("/admin/audit", f);
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="dash-audit">
      <DashHeader title="Registro attività" subtitle="Autore, data, valori precedenti e successivi delle modifiche importanti." />
      <div className="flex flex-wrap gap-2.5 mb-5">
        <input data-testid="audit-search" placeholder="Cerca azione o autore" className="nx-input flex-1 min-w-[220px]" onChange={(e) => setF({ ...f, q: e.target.value || undefined, page: 1 })} />
        <select data-testid="audit-filter-entity" aria-label="Entità" className="nx-input w-auto" onChange={(e) => setF({ ...f, entity: e.target.value || undefined, page: 1 })}><option value="">Tutte le entità</option>{["event", "clan", "user", "news", "regulation", "page", "settings", "correction"].map((x) => <option key={x}>{x}</option>)}</select>
      </div>
      {error ? <ErrorState message={error} onRetry={reload} /> : loading && !data ? <Loader /> : data.items.length ? (
        <div className="nx-card divide-y divide-[#C6C6CE]/5">
          {data.items.map((a) => (
            <div key={a.id} className="p-4 text-sm" data-testid="audit-row">
              <button className="w-full flex flex-wrap items-center gap-3 text-left" onClick={() => setOpen(open === a.id ? null : a.id)}>
                <span className="font-num text-xs text-[#C6C6CE]/50 w-40">{fmtDateTime(a.created_at)}</span>
                <span className="text-[#d6b3ff] font-num text-xs">{a.action}</span>
                <span className="text-[#F5F5F7]">{a.actor_name}</span>
                {a.note && <span className="text-xs text-amber-200">{a.note}</span>}
              </button>
              {open === a.id && (
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <pre className="bg-[#050505] p-3 rounded text-[11px] text-[#C6C6CE]/70 overflow-auto max-h-60">Prima: {JSON.stringify(a.before, null, 2)}</pre>
                  <pre className="bg-[#050505] p-3 rounded text-[11px] text-[#C6C6CE]/70 overflow-auto max-h-60">Dopo: {JSON.stringify(a.after, null, 2)}</pre>
                </div>
              )}
            </div>
          ))}
          <div className="p-4"><Pagination page={data.page} pages={data.pages} onChange={(p) => setF({ ...f, page: p })} /></div>
        </div>
      ) : <EmptyState title="Nessuna attività registrata" />}
    </div>
  );
}
